import type { Customer } from '../../types/order';

type SearchableCustomer = {
  name: Customer['name'];
  email?: string | null;
  phone?: string | null;
  contactPerson?: string | null;
  address?: string | null;
  taxId?: string | null;
  regon?: string | null;
  parentName?: string | null;
};

const normalizeSearchValue = (value?: string | null) => (value || '').trim().toLowerCase();
const compactSearchValue = (value?: string | null) => normalizeSearchValue(value).replace(/[^a-z0-9]+/g, '');

const buildSearchFragments = (value?: string | null) => {
  const normalized = normalizeSearchValue(value);

  if (!normalized) {
    return [];
  }

  const compact = normalized.replace(/[^a-z0-9]+/g, '');
  const withoutCountryPrefix = compact.replace(/^[a-z]{2}/, '');

  return Array.from(
    new Set([normalized, compact, withoutCountryPrefix].filter(Boolean))
  );
};

export function buildCustomerSearchText(customer: SearchableCustomer) {
  return [
    ...buildSearchFragments(customer.name),
    ...buildSearchFragments(customer.parentName),
    ...buildSearchFragments(customer.taxId),
    ...buildSearchFragments(customer.email),
    ...buildSearchFragments(customer.phone),
    ...buildSearchFragments(customer.contactPerson),
    ...buildSearchFragments(customer.address),
    ...buildSearchFragments(customer.regon),
  ].join(' ');
}

export function matchesCustomerSearch(customer: SearchableCustomer, query: string) {
  const normalizedQuery = normalizeSearchValue(query);

  if (!normalizedQuery) {
    return true;
  }

  return buildCustomerSearchText(customer).includes(normalizedQuery);
}

export function matchesCustomerPickerSearch(
  customer: Pick<SearchableCustomer, 'name' | 'taxId'> & { parentName?: string | null },
  query: string
) {
  const normalizedQuery = normalizeSearchValue(query);
  const compactQuery = compactSearchValue(query);

  if (!normalizedQuery) {
    return true;
  }

  const normalizedName = normalizeSearchValue(customer.name);
  const compactName = compactSearchValue(customer.name);
  const normalizedTaxId = normalizeSearchValue(customer.taxId);
  const compactTaxId = compactSearchValue(customer.taxId);
  const compactTaxIdWithoutCountry = compactTaxId.replace(/^[a-z]{2}/, '');
  const normalizedParentName = normalizeSearchValue(customer.parentName);
  const compactParentName = compactSearchValue(customer.parentName);

  const nameMatches =
    normalizedName.includes(normalizedQuery) ||
    compactName.includes(compactQuery) ||
    normalizedParentName.includes(normalizedQuery) ||
    compactParentName.includes(compactQuery);

  const taxIdMatches =
    normalizedTaxId.includes(normalizedQuery) ||
    compactTaxId.includes(compactQuery) ||
    compactTaxIdWithoutCountry.includes(compactQuery);

  return nameMatches || taxIdMatches;
}
